import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";

const BRAND = "#8b5cf6";
const BRAND_LIGHT = "#ede9fe";
const TEXT = "#150e33";
const MUTED = "#6B7280";
const BORDER = "#E5E7EB";

type FaqItem = { q: string; a: React.ReactNode };

const FAQ_GROUPS: { category: string; items: FaqItem[] }[] = [
	{
		category: "About the product",
		items: [
			{
				q: "What is Stippl for Business?",
				a: "Stippl for Business is an AI-powered trip planning platform for travel agents. It is the only itinerary builder with a fully visual, drag-and-drop interface: designed from the ground up to make planning trips for clients as fast, intuitive, and enjoyable as possible. Agents can build stunning, detailed itineraries without dealing with clunky forms or rigid templates.",
			},
			{
				q: "Who is Stippl for Business designed for?",
				a: "Stippl for Business is built for travel agents who create bespoke itineraries for their clients. It suits independent agents and agencies of all sizes who want a modern, visual planning tool, rather than a complex back-office system.",
			},
			{
				q: "How is Stippl for Business different from Travefy or mTrip?",
				a: "Most itinerary tools are built around data entry and document generation. Stippl is different because it is designed around the agent's experience first: drag-and-drop, highly visual, and polished enough that planning a trip actually feels good. It's the only tool in the market built with this level of UX attention specifically for travel professionals.",
			},
		],
	},
	{
		category: "Features",
		items: [
			{
				q: "How does the drag-and-drop planner work?",
				a: "Stippl's planner lets you drag and drop days, activities, accommodation, and transport into a visual timeline. Everything updates instantly as you build, so you always see exactly what the client will experience. There are no rigid forms or templates: you shape the trip visually, the way it makes sense to you.",
			},
			{
				q: "Can I brand Stippl with my company's identity?",
				a: "Yes. Stippl for Business supports custom branding so your clients experience itineraries under your agency's identity: your colours, logo, and style throughout.",
			},
		],
	},
	{
		category: "Reels & maps",
		items: [
			{
				q: "Can I create branded travel reels in Stippl?",
				a: "Yes. Stippl lets you generate fully branded travel reels straight from your itinerary content. The reels carry your agency's branding, logo, colours, and style, so they're ready to share with clients or post on social media without needing any external editing tools.",
			},
			{
				q: "Does Stippl include maps in the itinerary?",
				a: "Yes. Stippl lets you embed a beautiful interactive map directly on your own website or existing itinerary pages, no coding required. It's a better-looking, much easier alternative to Google Maps embeds, and gives your website a polished, professional feel without any of the technical hassle.",
			},
		],
	},
	{
		category: "Team & collaboration",
		items: [
			{
				q: "Can multiple agents work on the same trip at the same time?",
				a: "Yes. Stippl for Business supports real-time collaboration, so multiple agents can work on the same itinerary simultaneously. Changes are reflected instantly, making it easy for teams to build and refine trips together without version conflicts.",
			},
			{
				q: "What does a finished itinerary look like to the client?",
				a: "Clients receive a beautifully designed, visual itinerary via a shareable link or as a PDF, both available. The web view shows the full trip day by day, with accommodation, activities, transport, and maps in a clean, modern layout. The PDF is equally polished and fully branded, so it looks great whether a client is browsing online or printing it out for the trip.",
			},
		],
	},
	{
		category: "Pricing & getting started",
		items: [
			{
				q: "How much does Stippl for Business cost?",
				a: (
					<>
						Visit{" "}
						<a href="/pricing" style={{ color: BRAND, textDecoration: "underline" }}>
							business.stippl.io/pricing
						</a>{" "}
						for current plans and pricing.
					</>
				),
			},
			{
				q: "How long does it take to build a trip in Stippl?",
				a: "It depends on the size of the trip, but the combination of AI itinerary generation and a reusable highlights database saves agents hours compared to planning from scratch. The AI produces a full day-by-day structure instantly, and every highlight you save builds a library that makes future trips faster. The more you use Stippl, the quicker it gets.",
			},
			{
				q: "Do I need technical skills to use Stippl?",
				a: "No. Stippl is designed to be immediately intuitive: if you can drag and drop, you can use it. There is no setup complexity, no coding, and no training required. Most agents are building their first trip within minutes of signing up.",
			},
			{
				q: "Can I import existing itineraries or trip data?",
				a: "Yes. You can import existing itineraries by uploading a PDF or pasting a link. Stippl will parse the content and bring it into the editor, so you can continue building from what you already have rather than starting from scratch.",
			},
			{
				q: "How do I get started?",
				a: (
					<>
						Sign up at{" "}
						<a href="https://business.stippl.io" style={{ color: BRAND, textDecoration: "underline" }}>
							business.stippl.io
						</a>
						. Onboarding guides you through setting up your agency profile, building your first trip, and inviting your team. The Stippl support team is available to help you get set up quickly.
					</>
				),
			},
		],
	},
];

/**
 * `as` exists because this renders in two places with different semantics: a section on the
 * B2B homepage, where `h2` is right and the page's `h1` is elsewhere, and the whole of
 * `/business/faq`, where it is the page's only heading. That second case shipped with no `h1`
 * at all on a URL in the sitemap.
 */
export function FaqSection({ as: Heading = "h2" }: { as?: "h1" | "h2" } = {}) {
	const [openKey, setOpenKey] = useState<string | null>(null);

	return (
		<section id="faq" className="relative px-6 pt-20 pb-24" style={{ backgroundColor: "#ffffff" }}>
			<div className="max-w-3xl mx-auto">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}
					className="text-center mb-14"
				>
					<span
						style={{
							display: "inline-block",
							background: BRAND_LIGHT,
							color: BRAND,
							fontFamily: "Poppins, sans-serif",
							fontSize: "0.8rem",
							fontWeight: 600,
							padding: "5px 14px",
							borderRadius: 999,
							marginBottom: 20,
							letterSpacing: "0.3px",
							textTransform: "uppercase",
						}}
					>
						FAQ
					</span>
					<Heading
						className="font-bold mb-4"
						style={{
							fontFamily: "Poppins, sans-serif",
							fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
							fontWeight: 700,
							lineHeight: 1.2,
							letterSpacing: "-0.5px",
							color: TEXT,
						}}
					>
						Frequently asked questions
					</Heading>
					<p
						style={{
							color: MUTED,
							fontSize: "1.05rem",
							lineHeight: 1.6,
							maxWidth: 520,
							margin: "0 auto",
						}}
					>
						Everything you need to know about Stippl for Business: the visual, drag-and-drop itinerary builder built for travel agents.
					</p>
				</motion.div>

				{FAQ_GROUPS.map((group, groupIdx) => (
					<div key={group.category}>
						<div
							style={{
								fontFamily: "Poppins, sans-serif",
								fontSize: "0.75rem",
								fontWeight: 600,
								textTransform: "uppercase",
								letterSpacing: "0.8px",
								color: BRAND,
								margin: groupIdx === 0 ? "0 0 16px" : "40px 0 16px",
							}}
						>
							{group.category}
						</div>

						<div className="flex flex-col" style={{ gap: 10 }}>
							{group.items.map((item, idx) => {
								const key = `${groupIdx}-${idx}`;
								const isOpen = openKey === key;
								return (
									<div
										key={item.q}
										style={{
											background: "#ffffff",
											border: `1px solid ${BORDER}`,
											borderRadius: 12,
											overflow: "hidden",
											transition: "box-shadow 0.2s",
											boxShadow: isOpen ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
										}}
									>
										<button
											onClick={() => setOpenKey(isOpen ? null : key)}
											className="w-full flex items-center justify-between text-left focus:outline-none"
											style={{
												background: "none",
												border: "none",
												outline: "none",
												cursor: "pointer",
												padding: "20px 24px",
												gap: 16,
												fontFamily: "Poppins, sans-serif",
												fontSize: "1rem",
												fontWeight: 600,
												color: TEXT,
												lineHeight: 1.4,
											}}
											aria-expanded={isOpen}
										>
											<span>{item.q}</span>
											<motion.span
												animate={{
													rotate: isOpen ? 45 : 0,
													backgroundColor: isOpen ? BRAND : BRAND_LIGHT,
													color: isOpen ? "#ffffff" : BRAND,
												}}
												transition={{ duration: 0.2 }}
												aria-hidden="true"
												style={{
													flexShrink: 0,
													width: 24,
													height: 24,
													borderRadius: "50%",
													display: "inline-flex",
													alignItems: "center",
													justifyContent: "center",
													fontSize: "1.1rem",
													fontWeight: 400,
													lineHeight: 1,
												}}
											>
												+
											</motion.span>
										</button>
										<AnimatePresence initial={false}>
											{isOpen ? (
												<motion.div
													key="body"
													initial={{ height: 0, opacity: 0 }}
													animate={{ height: "auto", opacity: 1 }}
													exit={{ height: 0, opacity: 0 }}
													transition={{ duration: 0.22, ease: "easeOut" }}
													style={{ overflow: "hidden" }}
												>
													<div
														style={{
															padding: "16px 24px 20px",
															color: MUTED,
															fontSize: "0.95rem",
															lineHeight: 1.7,
															borderTop: `1px solid ${BORDER}`,
															fontFamily: "Poppins, sans-serif",
														}}
													>
														{item.a}
													</div>
												</motion.div>
											) : null}
										</AnimatePresence>
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
		</section>
	);
}
